import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ApiError,
  getWorks,
  type Work,
} from "../../obras/obrasApi";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar as obras.";
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Não foi possível carregar as obras.";
};

const clampPercent = (value?: number | null): number =>
  Math.max(0, Math.min(100, Math.round(value ?? 0)));

// Vista "Campo": ponto de entrada do encarregado. Lista as obras em cartões
// grandes, tocáveis, otimizados para telemóvel (mobile-first, só leitura).
export function CampoObrasView() {
  const navigate = useNavigate();
  const [works, setWorks] = useState<Work[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await getWorks();
        if (!isMounted) return;
        setWorks(response);
      } catch (error) {
        if (!isMounted) return;
        setWorks([]);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const shown = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return works;
    return works.filter((work) =>
      [work.name, work.code, work.clientName, work.location ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [works, filter]);

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div>
        <h2>Campo</h2>
        <p className="text-secondary-text mt-1">
          Escolha uma obra para ver os dados do terreno.
        </p>
      </div>

      <input
        type="text"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder="Filtrar obras…"
        className="w-full px-3 py-2 min-h-[44px] border border-border rounded bg-background text-foreground"
        style={{ borderRadius: "var(--radius)" }}
      />

      {loadError && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {loadError}
        </div>
      )}

      {isLoading && (
        <div
          className="border border-border bg-card px-4 py-3 text-secondary-text"
          style={{ borderRadius: "var(--radius)" }}
        >
          A carregar obras…
        </div>
      )}

      {!isLoading && !loadError && shown.length === 0 && (
        <div
          className="border border-border bg-card px-4 py-6 text-secondary-text text-center"
          style={{ borderRadius: "var(--radius)" }}
        >
          {filter.trim() !== ""
            ? "Sem resultados para o filtro."
            : "Sem obras para apresentar."}
        </div>
      )}

      {!isLoading && !loadError && shown.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shown.map((work) => {
            const progresso = clampPercent(work.progressPercent);
            return (
              <button
                key={work.id}
                type="button"
                onClick={() => navigate(`/campo/${work.id}`)}
                className="text-left bg-card border border-border p-4 hover:shadow-md transition-all cursor-pointer"
                style={{
                  borderRadius: "var(--radius-card)",
                  boxShadow: "var(--elevation-sm)",
                }}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <span
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      {work.code}
                    </span>
                    <h4 className="truncate">{work.name}</h4>
                    <p className="text-secondary-text mt-1 truncate">
                      {work.clientName}
                    </p>
                  </div>
                  <span
                    className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary flex-shrink-0"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {work.statusName?.trim() || "—"}
                  </span>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Progresso
                    </span>
                    <span
                      style={{
                        fontWeight: "var(--font-weight-semi-bold)",
                        fontSize: "var(--text-caption)",
                      }}
                    >
                      {progresso}%
                    </span>
                  </div>
                  <div
                    className="w-full bg-muted rounded-full h-2"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{
                        width: `${progresso}%`,
                        borderRadius: "var(--radius)",
                      }}
                    ></div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
